/**
 * Cart drawer extras
 * - <cart-upsell-add>: variant <select> swaps price, "Add" button adds the selected variant.
 * - <shipping-protection-toggle>: toggle adds/removes a real protection product; auto-adds by default.
 *
 * Reuses the theme globals: Shopify.addItem / Shopify.getCart (assets/global.js),
 * window.sharedFunctions.updateSidebarCart (assets/theme.js), fetchConfig() and window.routes.
 * Both elements are custom elements, so they re-upgrade automatically after the cart drawer
 * is re-rendered via the ajax_side_cart view.
 */
(function () {
  'use strict';

  function jq() {
    return window.jQuery || window.$ || null;
  }

  // Re-fetch the cart and re-render the side cart drawer using the theme's own pipeline.
  function refreshSideCart() {
    if (typeof Shopify === 'undefined' || typeof Shopify.getCart !== 'function') return;
    Shopify.getCart(function (cart) {
      if (window.sharedFunctions && typeof window.sharedFunctions.updateSidebarCart === 'function') {
        window.sharedFunctions.updateSidebarCart(cart);
      }
    });
  }

  function addVariant(variantId, qty, $btn) {
    var $ = jq();
    if (typeof Shopify !== 'undefined' && typeof Shopify.addItem === 'function' && $) {
      // Empty jQuery set for the 5th arg keeps Shopify.addItem's error handler safe
      // (it only touches `input` when input.length > 0).
      Shopify.addItem(variantId, qty || 1, $btn || $(), refreshSideCart, $());
      return;
    }
    if (window.routes && window.routes.cart_add_url && typeof fetchConfig === 'function') {
      fetch(window.routes.cart_add_url, Object.assign({}, fetchConfig(), {
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] })
      })).then(refreshSideCart);
    }
  }

  function removeVariant(variantId) {
    if (!(window.routes && window.routes.cart_change_url && typeof fetchConfig === 'function')) return;
    fetch(window.routes.cart_change_url, Object.assign({}, fetchConfig(), {
      body: JSON.stringify({ id: variantId, quantity: 0 })
    })).then(function () { refreshSideCart(); });
  }

  /* ------------------------------------------------------------------ *
   *  Upsell card
   * ------------------------------------------------------------------ */
  class CartUpsellAdd extends HTMLElement {
    constructor() {
      super();
      this._select = this.querySelector('[data-upsell-select]');
      this._addBtn = this.querySelector('[data-upsell-add]');
      this._priceNow = this.querySelector('[data-upsell-price]');
      this._priceWas = this.querySelector('[data-upsell-compare]');

      if (this._select) this._select.addEventListener('change', this.onVariantChange.bind(this));
      if (this._addBtn) this._addBtn.addEventListener('click', this.onAdd.bind(this));
    }

    onVariantChange() {
      var opt = this._select.options[this._select.selectedIndex];
      if (!opt) return;
      this.dataset.variantId = opt.value;
      if (this._priceNow && opt.dataset.price) this._priceNow.textContent = opt.dataset.price;
      if (this._priceWas) {
        if (opt.dataset.hasCompare === '1' && opt.dataset.compare) {
          this._priceWas.textContent = opt.dataset.compare;
          this._priceWas.style.display = '';
        } else {
          this._priceWas.style.display = 'none';
        }
      }
    }

    onAdd() {
      var variantId = this.dataset.variantId;
      if (!variantId || this._addBtn.classList.contains('is-loading')) return;
      this._addBtn.classList.add('is-loading');
      var $ = jq();
      addVariant(variantId, 1, $ ? $(this._addBtn) : null);
    }
  }
  if (!customElements.get('cart-upsell-add')) customElements.define('cart-upsell-add', CartUpsellAdd);

  /* ------------------------------------------------------------------ *
   *  Shipping protection toggle
   * ------------------------------------------------------------------ */
  var OPT_OUT_KEY = 'shippingProtectionOptOut';
  var protectionBusy = false;

  function optedOut() {
    try { return localStorage.getItem(OPT_OUT_KEY) === '1'; } catch (e) { return false; }
  }
  function setOptOut(v) {
    try { v ? localStorage.setItem(OPT_OUT_KEY, '1') : localStorage.removeItem(OPT_OUT_KEY); } catch (e) {}
  }

  class ShippingProtectionToggle extends HTMLElement {
    connectedCallback() {
      // A fresh render means any previous operation has settled.
      protectionBusy = false;

      this.variantId = this.dataset.variantId;
      var enabled = this.dataset.enabled === '1';
      var defaultOn = this.dataset.defaultOn === '1';
      var inCart = this.dataset.inCart === '1';
      var otherItems = parseInt(this.dataset.otherItems || '0', 10);
      this.checkbox = this.querySelector('input[type="checkbox"]');

      if (!this.variantId || !enabled) return;

      // Self-heal: protection should never be the only thing left in the cart.
      if (inCart && otherItems === 0) {
        this.remove();
        return;
      }

      if (this.checkbox) {
        this.checkbox.checked = inCart;
        if (!this._bound) {
          this.checkbox.addEventListener('change', this.onToggle.bind(this));
          this._bound = true;
        }
      }

      // Auto-add the first time the cart has real items, unless the customer opted out.
      if (defaultOn && !inCart && !optedOut() && otherItems > 0) {
        if (this.checkbox) this.checkbox.checked = true;
        this.add();
      }
    }

    onToggle() {
      if (this.checkbox.checked) {
        setOptOut(false);
        this.add();
      } else {
        setOptOut(true);
        this.remove();
      }
    }

    add() {
      if (protectionBusy || !this.variantId) return;
      protectionBusy = true;
      this._safety();
      var $ = jq();
      addVariant(this.variantId, 1, $ ? $(this) : null);
    }

    remove() {
      if (protectionBusy || !this.variantId) return;
      protectionBusy = true;
      this._safety();
      removeVariant(this.variantId);
    }

    // Failsafe so a network error never leaves the toggle stuck.
    _safety() {
      setTimeout(function () { protectionBusy = false; }, 6000);
    }
  }
  if (!customElements.get('shipping-protection-toggle')) {
    customElements.define('shipping-protection-toggle', ShippingProtectionToggle);
  }
})();
